import React, { useRef, useState } from 'react';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  availableTags: string[];
  placeholder?: string;
  className?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  availableTags,
  placeholder = 'Tag hinzufügen...',
  className = '',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSuggestions = availableTags.filter(
    (t) =>
      t.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedTags.includes(t)
  );

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onChange([...selectedTags, trimmed]);
    }
    setInputValue('');
    setShowDropdown(false);
  };

  const removeTag = (tag: string) => {
    onChange(selectedTags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const showSuggestions =
    showDropdown && (filteredSuggestions.length > 0 || inputValue.trim().length > 0);

  return (
    <div className={`relative ${className}`}>
      <div
        className="flex flex-wrap gap-1 p-2 border rounded min-h-[38px] focus-within:ring-2 focus-within:ring-orange-500 bg-white cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="hover:text-orange-600 font-bold leading-none"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
        />
      </div>

      {showSuggestions && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              onMouseDown={() => addTag(tag)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center gap-2"
            >
              <span className="w-3 h-3 rounded-full bg-orange-200 inline-block flex-shrink-0" />
              {tag}
            </button>
          ))}
          {inputValue.trim() && !availableTags.includes(inputValue.trim()) && (
            <button
              type="button"
              onMouseDown={() => addTag(inputValue)}
              className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 font-medium border-t border-gray-100"
            >
              + &quot;{inputValue.trim()}&quot; als neuen Tag erstellen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
